// Interior of a learning-path outcome card (ocb- prefix): score ring + name/description head,
// the five tone-coded action buttons, and the personalized-assignment score column. Extracted
// verbatim from LearningPathScreenStyles.ts (rbh- interior rules) so the SAME card body renders
// on the main path and inside the readiness view. The trail shell (row/card/stop/tints) stays
// with LearningPathScreen.
export const outcomeCardBodyStyles = `
  /* Action button — design lp-act: a glossy per-action gradient bubble + label.
     The bubble gradient + glyph color are set inline per action (5 design
     colors: teal / purple / blue / coral / gold); the base below is the shared
     glass gloss/edge. Disabled (content unavailable) dims via .ocb-act:disabled. */
  .ocb-act {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 7px;
    width: 58px;
    padding: 4px 2px;
    background: none;
    border: none;
    cursor: pointer;
  }
  .ocb-act:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .ocb-act-ic {
    position: relative;
    width: 48px;
    height: 48px;
    border-radius: var(--rad-catchup-full);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.7);
    /* NO backdrop-filter here — STYLING.md "Glass on scroll surfaces": a surface rendered
       once per list row / card must use --glass-flat-solid, never a per-item blur. There are
       FIVE of these per outcome card, so a grade-6 path carries ~600 of them down a ~41,000px
       page; each blur is its own backdrop-sampling pass, and every repaint of the column made
       all in-view bubbles re-sample at once. That is what starved raster of the tiles a scroll
       needs and left newly exposed cards unpainted. The frosted look is preserved by layering
       the per-action gradient over --glass-flat-solid (near-opaque) in OutcomeCardBody's inline
       background — the same trick .rbh-unit-pill uses. Removing the blur only pays off with the
       trail pulse hoisted out of the full-column svg (LearningPathScreenStyles.ts): with the
       column still repainting every frame, dropping these layers made paint WORSE (2649ms ->
       7646ms traced). Fix both or neither. */
    box-shadow:
      inset 0 2px 3px rgba(255, 255, 255, 0.8),
      inset 0 -7px 12px -7px rgba(15, 72, 89, 0.22),
      0 4px 10px -4px rgba(15, 72, 89, 0.22);
    transition:
      transform 0.2s var(--ease-glass),
      box-shadow 0.2s var(--ease-glass);
  }
  .ocb-act-gloss {
    position: absolute;
    inset: 0;
    border-radius: var(--rad-catchup-full);
    pointer-events: none;
    background: linear-gradient(
      150deg,
      rgba(255, 255, 255, 0.7) 0%,
      rgba(255, 255, 255, 0) 50%
    );
  }
  /* A second, tighter specular on top of the broad gloss — the highlight that makes the bubble
     read as a glass bead rather than a flat gradient disc. */
  .ocb-act-shine {
    position: absolute;
    top: 7px;
    left: 10px;
    width: 17px;
    height: 9px;
    border-radius: var(--rad-catchup-full);
    pointer-events: none;
    transform: rotate(-20deg);
    background: linear-gradient(
      120deg,
      rgba(255, 255, 255, 0.85),
      rgba(255, 255, 255, 0)
    );
  }
  /* Glyph wrapper — the element the idle motion animates, so the transform is not written onto
     the 48px bubble and its three box-shadows. The filtered <svg> is still INSIDE it, so this
     does not take the filter out of the transformed subtree; what keeps the cost down is the
     scope of the animation (see below), not this split. */
  .ocb-act-g {
    position: relative;
    z-index: 1;
    display: flex;
  }
  .ocb-act-ic svg {
    filter: drop-shadow(0 1px 1px rgba(255, 255, 255, 0.5));
  }
  .ocb-act:not(:disabled):hover .ocb-act-ic {
    transform: translateY(-3px);
    box-shadow:
      inset 0 2px 3px rgba(255, 255, 255, 0.85),
      inset 0 -7px 12px -7px rgba(15, 72, 89, 0.2),
      0 10px 18px -6px rgba(15, 72, 89, 0.3);
  }
  .ocb-act:not(:disabled):active .ocb-act-ic {
    transform: translateY(-1px);
  }
  /* Idle micro-motion — one gesture per action, so the row reads as five different
     invitations instead of one throbbing set. Rules behind it, all deliberate:
       - TRANSFORM AND OPACITY ONLY. No filter / box-shadow / backdrop animation: the war
         story on .ocb-act-ic above is what per-orb compositing costs when multiplied by the
         ~600 orbs a grade-6 path carries.
       - The animated node is the bare .ocb-act-g span; the filtered svg sits under it.
       - Periods are long and mutually offset, with a stagger, so the five orbs on one card
         never lock into a single pulse.
       - Disabled orbs hold still. They are not offering anything.
     SCOPED TO .rdz-spy-card ON PURPOSE — the readiness chain, which shows exactly ONE card at a
     time, so five animations run. Unscoped these reach every OutcomeCardBody, and the learning
     path renders its outcomes unvirtualized: ~120 cards, ~600 orbs, down a 40,907px column. That
     surface has already starved raster once from a SINGLE permanent animation (673 -> 48 paint
     events, 737 -> 24 dropped frames; see the trail-pulse note in LearningPathScreenStyles.ts),
     and it shipped with none. The gloss and shine above are static and still apply everywhere;
     only the motion is held back. Widening this selector needs a paint trace on a full grade-6
     path first, not an assumption. */
  @media (prefers-reduced-motion: no-preference) {
    .rdz-spy-card .ocb-act:not(:disabled) .ocb-act-idle-breathe {
      animation: ocb-idle-breathe 4.4s ease-in-out infinite;
    }
    .rdz-spy-card .ocb-act:not(:disabled) .ocb-act-idle-wave {
      animation: ocb-idle-wave 3.6s ease-in-out 0.35s infinite;
    }
    .rdz-spy-card .ocb-act:not(:disabled) .ocb-act-idle-tip {
      animation: ocb-idle-tip 5.2s ease-in-out 0.7s infinite;
    }
    .rdz-spy-card .ocb-act:not(:disabled) .ocb-act-idle-write {
      animation: ocb-idle-write 4.8s ease-in-out 1.05s infinite;
    }
    .rdz-spy-card .ocb-act:not(:disabled) .ocb-act-idle-twinkle {
      animation: ocb-idle-twinkle 3.9s ease-in-out 1.4s infinite;
    }
  }
  @keyframes ocb-idle-breathe {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.07); }
  }
  @keyframes ocb-idle-wave {
    0%, 100% { transform: scale(0.94); }
    50% { transform: scale(1.08); }
  }
  @keyframes ocb-idle-tip {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-1.5px) rotate(2.5deg); }
  }
  @keyframes ocb-idle-write {
    0%, 100% { transform: rotate(0deg); }
    42% { transform: rotate(-7deg); }
    68% { transform: rotate(4deg); }
  }
  @keyframes ocb-idle-twinkle {
    0%, 100% { transform: scale(0.9) rotate(0deg); opacity: 0.82; }
    50% { transform: scale(1.1) rotate(18deg); opacity: 1; }
  }
  .ocb-act-pill {
    position: absolute;
    top: -3px;
    right: -3px;
    width: 17px;
    height: 17px;
    border-radius: var(--rad-catchup-full);
    background-color: var(--catchup-white);
    color: var(--catchup-hover-green);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(15, 72, 89, 0.18);
  }
  .ocb-act-label {
    font-size: var(--fs-md);
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    color: var(--catchup-blue-700);
    max-width: 58px;
  }
  /* --- prerequisite species -------------------------------------------------
     Same anatomy as an outcome card, different MATERIAL: the shell that wraps
     this body carries .ocb-prereq, which repaints the card sand + recessed so
     the learner can tell the two planes apart. Teal = a stop on your path;
     sand = the foundation under one. Kept clear of the coral lock hue. */
  .ocb-prereq-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    font-size: var(--fs-sm);
    font-weight: 800;
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: #96814f;
  }
  /* Grade badge in the prerequisite eyebrow — "6. Sınıf". Usually an EARLIER grade than the
     learner's, which is most of the explanation for the gap. */
  .ocb-prereq-grade {
    padding: 2px 8px;
    border-radius: var(--rad-catchup-full);
    background-color: #efe6d2;
    color: #7a6535;
    letter-spacing: 0.04em;
  }
  .ocb-prereq .ocb-outcome-actions {
    border-color: rgba(201, 167, 94, 0.3);
  }
  .ocb-prereq .ocb-act-label {
    color: #7a6535;
  }
  .ocb-prereq .ocb-chip {
    background-color: #efe6d2;
    color: #96814f;
  }
  /* Stands in for the readiness bar on a not-yet-measured outcome. */
  .ocb-measure-hint {
    margin: 0.75rem 0 0;
    padding: 7px 12px;
    border-radius: var(--rad-catchup-medium);
    background-color: #f3ecdd;
    border: 1px dashed #ddcfae;
    color: #8a7a56;
    font-size: var(--fs-sm);
  }

  /* Neutral status pill in the here-badge slot (readiness view: "Başlanmadı") */
  .ocb-chip {
    display: inline-flex;
    align-items: center;
    margin-bottom: 6px;
    padding: 3px 10px;
    border-radius: var(--rad-catchup-full);
    background-color: var(--catchup-gray-100);
    color: var(--catchup-gray-400);
    font-size: var(--fs-xs);
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  /* Readiness bar — the bottom strip that opens the prerequisite drill-down. Locked = coral
     urgency tint; ready = subtle green confirmation. It answers four things at once: what this
     is, what to do about it, how far along, and the way in. The full-pill radius the earlier
     single-line version used cannot hold two lines of text, so this is the large radius with
     the progress line running flush along the bottom edge. */
  .ocb-readiness {
    position: relative;
    overflow: hidden;
    margin-top: 0.75rem;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 8px 12px 11px;
    border-radius: var(--rad-catchup-large);
    border: 1px solid rgba(240, 158, 126, 0.5);
    background: linear-gradient(180deg, rgba(252, 224, 210, 0.6), rgba(252, 224, 210, 0.28));
    color: #b4552f;
    font-size: var(--fs-sm);
    font-weight: 700;
    text-align: left;
    cursor: pointer;
    transition:
      border-color 0.2s var(--ease-glass),
      box-shadow 0.2s var(--ease-glass);
  }
  .ocb-readiness:disabled {
    cursor: default;
  }
  /* Hover deepens the border and the shadow; it does NOT translate the strip. A transform on
     a surface this size paints a stacking context the learning path's confirm-modal backdrop
     blur samples as a bare white rectangle. The chevron alone moves — it is small enough to
     stay inside the strip's own paint. */
  .ocb-readiness:not(:disabled):hover {
    border-color: rgba(240, 158, 126, 0.85);
    box-shadow: 0 6px 14px -6px rgba(216, 97, 63, 0.35);
  }
  .ocb-readiness-ready {
    border-color: rgba(122, 193, 66, 0.4);
    background: linear-gradient(180deg, rgba(228, 245, 214, 0.6), rgba(228, 245, 214, 0.3));
    color: var(--catchup-hover-green);
  }
  .ocb-readiness-ready:not(:disabled):hover {
    border-color: rgba(122, 193, 66, 0.75);
    box-shadow: 0 6px 14px -6px rgba(90, 160, 60, 0.3);
  }
  /* Icon chip — glass WITHOUT the blur, for the same reason .ocb-act-ic dropped it: a
     near-opaque wash under a white inner highlight reads the same and costs nothing per row. */
  .ocb-readiness-ic {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: var(--rad-catchup-full);
    border: 1px solid rgba(242, 134, 95, 0.42);
    background: linear-gradient(158deg, rgba(255, 255, 255, 0.95), rgba(255, 224, 208, 0.82));
    box-shadow:
      inset 0 1px 2px rgba(255, 255, 255, 0.95),
      0 2px 6px -2px rgba(217, 97, 63, 0.45);
  }
  .ocb-readiness-ready .ocb-readiness-ic {
    border-color: rgba(122, 193, 66, 0.45);
    background: linear-gradient(158deg, rgba(255, 255, 255, 0.95), rgba(228, 245, 214, 0.85));
    box-shadow:
      inset 0 1px 2px rgba(255, 255, 255, 0.95),
      0 2px 6px -2px rgba(96, 150, 50, 0.4);
  }
  .ocb-readiness-txt {
    flex: 1 1 0%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .ocb-readiness-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ocb-readiness-sub {
    font-size: var(--fs-xs);
    font-weight: 600;
    opacity: 0.85;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ocb-readiness-count {
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }
  /* The whole strip is the button, which on its own reads as a banner. The pill is what says
     it is a control. No text-transform: uppercasing "Git" without a Turkish locale yields
     "GIT" rather than "GIT" with the dotted capital, which is a misspelling in Turkish. */
  .ocb-readiness-go {
    flex-shrink: 0;
    padding: 4px 11px;
    border-radius: var(--rad-catchup-full);
    border: 1px solid rgba(242, 134, 95, 0.5);
    background-color: rgba(255, 255, 255, 0.62);
    font-size: var(--fs-xs);
    font-weight: 800;
    letter-spacing: 0.03em;
  }
  .ocb-readiness-ready .ocb-readiness-go {
    border-color: rgba(122, 193, 66, 0.5);
  }
  .ocb-readiness-chevron {
    font-size: var(--fs-lg);
    line-height: 1;
    flex-shrink: 0;
    transition: transform 0.2s var(--ease-glass);
  }
  .ocb-readiness:not(:disabled):hover .ocb-readiness-chevron {
    transform: translateX(3px);
  }
  /* Progress along the bottom edge: the same ready/total the count states, in a form that can
     be read without stopping to compare two numbers. */
  .ocb-readiness-track {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background-color: rgba(217, 97, 63, 0.16);
  }
  .ocb-readiness-fill {
    display: block;
    height: 100%;
    border-radius: 0 var(--rad-catchup-full) var(--rad-catchup-full) 0;
    background: linear-gradient(90deg, #f2b45a, #e8643b);
    transition: width 0.35s var(--ease-glass);
  }
  .ocb-readiness-ready .ocb-readiness-track {
    background-color: rgba(122, 193, 66, 0.2);
  }
  .ocb-readiness-ready .ocb-readiness-fill {
    background: linear-gradient(90deg, #acd747, #7ac142);
  }

  /* BURADASIN marker — a filled coral pill so it reads instantly */
  /* "Okulda bu hafta" — amber, so it never reads as the coral "Buradasın" position pill. */
  .ocb-week {
    display: inline-flex;
    align-items: center;
    margin: 0 6px 6px 0;
    padding: 3px 10px;
    border-radius: var(--rad-catchup-full);
    background: rgba(253, 230, 138, 0.7);
    border: 1px solid rgba(230, 160, 35, 0.5);
    color: #92400e;
    font-size: var(--fs-sm);
    font-weight: 800;
  }
  .ocb-here {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 6px;
    padding: 4px 11px;
    border-radius: var(--rad-catchup-full);
    background: linear-gradient(180deg, #f2865f, #e8643b);
    color: var(--catchup-white);
    font-size: var(--fs-xs);
    font-weight: 800;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.4), 0 4px 10px rgba(216, 97, 63, 0.32);
  }
  .ocb-outcome-head {
    display: flex;
    align-items: center;
    column-gap: 0.75rem;
    margin-bottom: 0.75rem;
    cursor: pointer;
  }
  .ocb-outcome-ring {
    flex-shrink: 0;
  }
  .ocb-outcome-textcol {
    flex: 1 1 0%;
    min-width: 0;
    text-align: left;
  }
  .ocb-outcome-name {
    font-weight: 600;
    font-size: var(--fs-lg);
    color: var(--text-primary);
    line-height: 1.25;
  }
  .ocb-outcome-desc {
    font-size: var(--fs-base);
    color: var(--text-muted);
    margin-top: 0.25rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.375;
  }
  .ocb-outcome-actions {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 4px;
    padding-top: 0.75rem;
    border-top-width: 1px;
    border-top-style: solid;
    border-color: rgba(84, 187, 203, 0.16);
  }
  .ocb-pa-col {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .ocb-pa-score {
    font-size: var(--fs-md);
    font-weight: 700;
    color: var(--catchup-hover-green);
    font-variant-numeric: tabular-nums;
    margin-top: 0.125rem;
  }
`;
